import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { IPreferences, PreferencesContext } from 'contexts/preferences';
import { buildClassName } from 'utils/build-class-name';
import { useUserContext } from 'contexts/user';
import useKeyboard from 'hooks/use-keyboard';
import WarningIcon from 'components/icon/warning-icon';
import LoadingSpinner from 'components/loading-spinner';
import LocationIcon from 'components/icon/location-icon';
import HomeIcon from 'components/icon/home-icon';
import NotificationIcon from 'components/icon/notification-icon';
import ClockIcon from 'components/icon/clock-icon';
import StarIcon from 'components/icon/star-icon';
import SparkleIcon from 'components/icon/sparkle-icon';
import PillBadge from 'components/atomic/organisms/pill-badge';
import { trackEvent } from 'utils/google-tag-manager';
import { testIds } from 'constants/test-constants';
import LocationDropdownSection from './location-dropdown-section';
import SearchSuggestionsBanner from './search-suggestions-banner';
import pushToRoute from 'utils/push-to-route';
import { useFeaturesContext, useThemeContext } from 'contexts';
import { getTenantById } from '@tenants';
import { useTenantId } from 'hooks/use-tenant-id';
import styles from './style.module.scss';
import { MapType } from 'data/search-predictions';
import useMapLocationChanger from 'hooks/use-map-location-changer';
import { ThemeNames } from 'types/themes';
import usePopularCities from 'hooks/use-popular-cities';
import { MILITARY_BASES_SEARCH_PREDICTION_GROUP } from 'data/search-predictions';
import { useTranslation } from 'next-i18next';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import { 
  getSmartChatBetaStatus,
  SmartChatBetaStatus,
  setSmartChatOptIn, 
  setSmartChatOptOut, 
} from 'utils/cookies';
import Cookies from 'js-cookie';
import { USE_AI_CHAT_COOKIE_NAME } from 'constants/cookies';
import { useIsDesktop } from 'hooks/use-size-class';
import { trackSmartchatOptIn, trackSmartchatOptOut } from 'utils/smartchat-tracking';

import type SearchPrediction from 'data/search-predictions';
import { convertSearchSuggestionToSearchPrediction } from 'data/search-predictions';

export type SearchSuggestions = {
  identifier: number;
  label: string;
  latitude?: number;
  longitude?: number;
  urlPath: string;
  hasAreaPage?: boolean;
  zoom: number;
};

export type SearchAgentPrediction = {
  identifier: number;
  label: string;
  group: string;
};

export type SearchResult = SearchPrediction | SearchSuggestions;

interface Props {
  searchPredictions: SearchPrediction[];
  setActivePrediction: (searchPrediction: any) => void;
  isActive: boolean;
  isLoading: boolean;
  isAreaSearch?: boolean;
  isAdvancedSearch?: boolean;
  locationQuery?: string;
  onClick: (searchPrediction: SearchPrediction) => void;
  className?: string;
  isTyping?: boolean;
  disablePopularSearches?: boolean;
  hideSearchPanel?: () => void;
  searchAgentPredictions?: SearchAgentPrediction[];
  fullWidth?: boolean;
}

export type SearchPredictionSortOptions = {
  prioritizeListings: boolean;
  isAddressQuery: boolean;
};
const hasStreetNumberAndName = (query: string) =>
  /^\s*\d+[A-Za-z0-9-]*\s+[A-Za-z]/.test(query);

export const getSearchPredictionSortOptions = (
  query: string,
  _predictions: SearchPrediction[]
): SearchPredictionSortOptions => {
  const isAddressQuery = hasStreetNumberAndName(query);

  return {
    isAddressQuery,
    prioritizeListings: isAddressQuery,
  };
};

export function sortSearchPredictions(
  searchPredictions: SearchPrediction[],
  options: Partial<SearchPredictionSortOptions> = {}
) {
  const { prioritizeListings = false } = options;
  const locations = searchPredictions.filter(
    searchPrediction => searchPrediction.group === 'locations'
  );
  const listings = searchPredictions.filter(
    searchPrediction => searchPrediction.group === 'listings'
  );
  const schools = searchPredictions.filter(
    searchPrediction => searchPrediction.group === 'schools'
  );
  const buildings = searchPredictions.filter(
    searchPrediction => searchPrediction.group === 'buildings'
  );
  const militaryBases = searchPredictions.filter(
    searchPrediction => searchPrediction.group === MILITARY_BASES_SEARCH_PREDICTION_GROUP
  );
  const orderedPredictions = [
    ...militaryBases,
    ...(prioritizeListings ? listings : locations),
    ...(prioritizeListings ? locations : listings),
    ...schools,
    ...buildings,
  ];
  orderedPredictions.forEach(
    (searchPrediction, index) => (searchPrediction.identifier = index)
  );
  return {
    locations,
    listings,
    schools,
    militaryBases,
  };
}

export default function SuggestedLocationDropdown({
  searchPredictions,
  setActivePrediction,
  isActive,
  isAreaSearch,
  isAdvancedSearch,
  locationQuery,
  isLoading,
  onClick,
  className,
  isTyping,
  hideSearchPanel,
  disablePopularSearches,
  searchAgentPredictions = [],
  fullWidth = false,
}: Props) {
  const { t } = useTranslation(Namespaces.SEARCH);
  const { recentSearches, setIsAiSearch } = useContext(PreferencesContext) as IPreferences;
  const { features } = useFeaturesContext();
  const { themeName } = useThemeContext();
  const tenantId = useTenantId();
  const tenant = getTenantById(tenantId);
  const router = useRouter();
  const isHomeAppraisal =
    router.asPath && router.asPath.includes('/home-appraisal');
  const normalizedQuery = locationQuery ?? '';
  const sortOptions = useMemo(
    () => getSearchPredictionSortOptions(normalizedQuery, searchPredictions),
    [normalizedQuery, searchPredictions]
  );
  const suggestedResults = useMemo(
    () => sortSearchPredictions(searchPredictions, sortOptions),
    [searchPredictions, sortOptions]
  );
  const [selectedResult, setSelectedResult] = useState(0);
  const resultHasContent =
    (!!searchPredictions.length || !!searchAgentPredictions.length) && !isLoading;
  const { user } = useUserContext();
  const updatedPopularSearches = usePopularCities();
  const filteredRecentSearches = useMemo(() => {
    if (!recentSearches) return [];
    if (isHomeAppraisal) return recentSearches.filter(s => s.label !== 'city');
    return recentSearches;
  }, [recentSearches, isHomeAppraisal]);
  const [isEnterKeyPressed, setIsEnterKeyPressed] = useState(false);
  
  // SmartChat Beta Banner visibility
  const [showBanner, setShowBanner] = useState(false);
  const isDesktop = useIsDesktop();
  
  useEffect(() => {
    // Show banner only on desktop (not mobile or tablet), if user has not opted in/out and AiSearch feature flag is enabled
    const aiSearchEnabled = tenant.features.AiSearch ?? false;
    const userOptedIn = (getSmartChatBetaStatus() === SmartChatBetaStatus.OPTED_IN);
    setShowBanner(aiSearchEnabled && !userOptedIn && !isLoading && isDesktop);
  }, [isLoading, tenant.features.AiSearch, isDesktop]);
  
  const showRecentSearches =
    isAdvancedSearch &&
    !locationQuery &&
    recentSearches &&
    !resultHasContent &&
    !isLoading &&
    !isAreaSearch;

  const notYetSearched =
    isAdvancedSearch && !locationQuery && !resultHasContent && !isLoading;
  const noResults = !resultHasContent && !isLoading && locationQuery;
  const showPopularSearches =
    (noResults || notYetSearched) && !disablePopularSearches;
  const showAgentPredictionSearches = !!searchAgentPredictions.length;

  const { listingParams } = useContext(PreferencesContext) as IPreferences;
  const map: MapType = useMapLocationChanger();

  const shouldPrioritizeListings = sortOptions.prioritizeListings;

  const computeFlattenedResults = (
    sorted: ReturnType<typeof sortSearchPredictions>,
    recent: SearchPrediction[],
    popular: SearchSuggestions[],
    agents: SearchAgentPrediction[],
    includeRecent: boolean,
    includePopular: boolean,
    includeAgents: boolean,
    prioritizeListings: boolean,
  ) => {
    const prioritizedGroupResults = prioritizeListings
      ? [...sorted.listings, ...sorted.locations]
      : [...sorted.locations, ...sorted.listings];
    const results: any[] = [
      ...sorted.militaryBases,
      ...prioritizedGroupResults,
      ...sorted.schools,
      ...(includeRecent ? recent : []),
      ...(includePopular ? popular : []),
      ...(includeAgents ? agents : []),
    ];
    results.forEach((item, index) => { item.identifier = index; });
    return results;
  };

  const [flattenedResults, setFlattenedResults] = useState<any[]>(() =>
    computeFlattenedResults(
      suggestedResults,
      filteredRecentSearches as SearchPrediction[],
      updatedPopularSearches,
      searchAgentPredictions,
      !!showRecentSearches,
      !!showPopularSearches,
      showAgentPredictionSearches,
      shouldPrioritizeListings,
    )
  );

  useEffect(() => {
    if (locationQuery?.length && !searchPredictions.length && !isLoading) {
      trackEvent('no-results', 'search-results', locationQuery);
    }
  }, [locationQuery, searchPredictions.length, isLoading]);

  useEffect(() => {
    const newFlattened = computeFlattenedResults(
      suggestedResults,
      filteredRecentSearches as SearchPrediction[],
      updatedPopularSearches,
      searchAgentPredictions,
      !!showRecentSearches,
      !!showPopularSearches,
      showAgentPredictionSearches,
      shouldPrioritizeListings,
    );
    setFlattenedResults(newFlattened);
    setSelectedResult(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedResults, filteredRecentSearches, updatedPopularSearches, searchAgentPredictions, showRecentSearches, showPopularSearches, showAgentPredictionSearches, shouldPrioritizeListings]);

  useEffect(() => {
    if (flattenedResults.length > 0) {
      setActivePrediction(flattenedResults[selectedResult] ?? flattenedResults[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flattenedResults, selectedResult]);

  useEffect(() => {
    if (isEnterKeyPressed) {
      navigateToEnteredLocation();
      setIsEnterKeyPressed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnterKeyPressed]);

  const navigateDownListWithKeyboard = () => {
    if (isActive) {
      setSelectedResult(prev =>
        Math.min(prev + 1, flattenedResults.length - 1)
      );
    }
  };
  const navigateUpListWithKeyboard = () => {
    if (isActive) {
      setSelectedResult(prev => Math.max(prev - 1, 0));
    }
  };
  const navigateToEnteredLocation = async () => {
    if (!isActive) return;
    const selectedLocation = flattenedResults[selectedResult];
    if (!selectedLocation) return;

    setActivePrediction(selectedLocation);
    const isSearchSuggestion =
      (selectedLocation as Record<string, unknown>)['group'] === undefined;
    if (isSearchSuggestion) {
      const searchPrediction = await convertSearchSuggestionToSearchPrediction(
        selectedLocation as SearchSuggestions
      ) as SearchPrediction;
      onClick(searchPrediction);
    } else {
      onClick(selectedLocation as SearchPrediction);
    }
  };

  useKeyboard(navigateDownListWithKeyboard, 'arrowdown');
  useKeyboard(navigateUpListWithKeyboard, 'arrowup');
  useKeyboard(() => {
    const input: HTMLInputElement | null =
      document.querySelector('#locationInput');
    // Needed to get input value this way because locationQuery gets emptied for some reason when using the keyboard hook
    if (!input?.value.length) {
      setIsEnterKeyPressed(true);
    } else {
      resultHasContent &&
        !isLoading &&
        !!searchPredictions.length &&
        !isTyping &&
        setIsEnterKeyPressed(true);
    }
  }, 'enter');

  const handlePushToRoute = (searchSuggestion: SearchSuggestions) => {
    hideSearchPanel?.();
    pushToRoute(
      searchSuggestion,
      listingParams,
      router,
      themeName as ThemeNames,
      map,
      features.useUsListings,
      user!
    );
  };

  return !isActive ? null : (
    <div
      className={buildClassName(
        styles.component,
        className,
        isAreaSearch && styles['component-small-width'],
        fullWidth && styles['component-full-width'],
        isAdvancedSearch && styles['isAdvancedSearch']
      )}
      data-testid={testIds.suggestedLocationsDropdown}
    >
      {showBanner && (
        <SearchSuggestionsBanner
          onDismiss={() => {
            // Track smart chat beta opt-out event in GTM
            trackSmartchatOptOut();
            
            // User opts out - set cookie for 7 days
            setSmartChatOptOut();
            setShowBanner(false);
          }}
        >
          <div className={styles['banner-content']}>
            <div className={styles['banner-icon']}>
              <SparkleIcon />
            </div>
            <div className={styles['banner-text']}>
              <div className={styles['banner-title-row']}>
                <h3>{t(LOCALE_SEARCH_PATHS.AI_SEARCH_BANNER_TITLE)}</h3>
                <PillBadge
                  backgroundColor="var(--eXp-Secondary-Brand-Royal, #1B489B)"
                  textColor="#FFFFFF"
                  size="small"
                  className={styles['banner-beta-badge']}
                >
                  BETA
                </PillBadge>
              </div>
              <p>{t(LOCALE_SEARCH_PATHS.AI_SEARCH_BANNER_DESCRIPTION)}</p>
            </div>
            <button
              className={styles['try-now-button']}
              onClick={() => {
                // Track smart chat beta opt-in event in GTM
                trackSmartchatOptIn();
                
                // User opts in - set cookie preference
                setSmartChatOptIn();
                setShowBanner(false);
                
                Cookies.set(USE_AI_CHAT_COOKIE_NAME, 'true', { path: '/' });
                setIsAiSearch(true);
                router.push('/search');
              }}
              aria-label={t(LOCALE_SEARCH_PATHS.AI_SEARCH_BANNER_TRY_NOW_BUTTON)}
            >
              {t(LOCALE_SEARCH_PATHS.AI_SEARCH_BANNER_TRY_NOW_BUTTON)}
            </button>
          </div>
        </SearchSuggestionsBanner>
      )}
      {isLoading && <LoadingSpinner className={styles['loading-spinner']} />}
      {noResults && locationQuery.length > 2 && !isTyping && (
        <span
          className={styles['warning']}
        >{t(LOCALE_SEARCH_PATHS.DROPDOWN_NO_RESULTS, { 
            defaultValue: 'Sorry, there are no results for "{{query}}"',
            query: locationQuery, 
          })}</span>
      )}
      {resultHasContent && (
        <>
          {suggestedResults.militaryBases.length > 0 && (
            <LocationDropdownSection
              title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_MILITARY_BASES, { defaultValue: 'Military Bases' })}
              Icon={LocationIcon}
              selectedResult={selectedResult}
              searchPredictions={suggestedResults.militaryBases}
              onClick={onClick}
            />
          )}
          {shouldPrioritizeListings ? (
            <>
              {!isAreaSearch && (
                <LocationDropdownSection
                  title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_LISTINGS, { defaultValue: 'Listings' })}
                  Icon={HomeIcon}
                  selectedResult={selectedResult}
                  searchPredictions={suggestedResults.listings}
                  onClick={onClick}
                />
              )}
              <LocationDropdownSection
                title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_LOCATIONS, { defaultValue: 'Locations' })}
                Icon={LocationIcon}
                selectedResult={selectedResult}
                searchPredictions={suggestedResults.locations}
                onClick={onClick}
              />
            </>
          ) : (
            <>
              <LocationDropdownSection
                title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_LOCATIONS, { defaultValue: 'Locations' })}
                Icon={LocationIcon}
                selectedResult={selectedResult}
                searchPredictions={suggestedResults.locations}
                onClick={onClick}
              />
              {!isAreaSearch && (
                <LocationDropdownSection
                  title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_LISTINGS, { defaultValue: 'Listings' })}
                  Icon={HomeIcon}
                  selectedResult={selectedResult}
                  searchPredictions={suggestedResults.listings}
                  onClick={onClick}
                />
              )}
            </>
          )}
          <LocationDropdownSection
            title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_SCHOOLS, { defaultValue: 'Schools' })}
            Icon={NotificationIcon}
            selectedResult={selectedResult}
            searchPredictions={suggestedResults.schools}
            onClick={onClick}
          />
        </>
      )}
      {showRecentSearches && (
        <LocationDropdownSection
          title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_RECENT_SEARCHES, { defaultValue: 'Recent Searches' })}
          Icon={ClockIcon}
          selectedResult={selectedResult}
          searchPredictions={filteredRecentSearches as SearchPrediction[]}
          onClick={onClick}
        />
      )}
      {showPopularSearches && (
        <LocationDropdownSection
          title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_POPULAR_SEARCHES, { defaultValue: 'Popular Searches' })}
          Icon={StarIcon}
          selectedResult={selectedResult}
          searchPredictions={[]}
          searchSuggestions={updatedPopularSearches}
          onClick={onClick}
          pushToRoute={handlePushToRoute}
        />
      )}
      {showAgentPredictionSearches && (
        <LocationDropdownSection
          title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_LOCATIONS, { defaultValue: 'Locations' })}
          Icon={StarIcon}
          selectedResult={selectedResult}
          searchPredictions={[]}
          searchAgentPredictions={searchAgentPredictions}
          onClick={onClick}
          pushToRoute={handlePushToRoute}
        />
      )}
      {(!showPopularSearches ||
        (noResults && locationQuery.length > 2 && !isTyping)) && (
        <span className={styles['warning']}>
          <WarningIcon />
          {t(LOCALE_SEARCH_PATHS.DROPDOWN_OUT_OF_SERVICE_AREA, { 
            defaultValue: "Don't see what you're looking for? Your search might be out of our service area",
          })}
        </span>
      )}
    </div>
  );
}
